
'use client';

import { useState, useEffect } from 'react';
import { GradientText } from '@/components/ui/gradient-text';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import Link from 'next/link';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Loader2 } from 'lucide-react';

interface Project {
  id: number;
  title: string;
  description: string;
  image_url: string;
  technologies: string;
  project_url: string;
  source_code_url: string;
  work_stream: string;
  status: string;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL;

async function fetchProjects(): Promise<{
  success: boolean;
  data?: Project[];
  message?: string;
}> {
  try {
    const url = `${API_BASE_URL}/api/projects`;
    const response = await fetch(url, { next: { revalidate: 600 } });
    console.log("Response status:", response);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred.' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    const result = await response.json();
    return { success: true, data: result.success ? result.data : [] };
  } catch (error: any) {
    const errorMessage = error.message || "An unknown fetch error occurred.";
    console.error("Error fetching projects:", errorMessage);
    return { success: false, message: errorMessage };
  }
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadProjects() {
      setIsLoading(true);
      setError(null);
      const { data, success, message } = await fetchProjects();
      if (success && data) {
        setProjects(data);
      } else {
        setError(message || 'Failed to load projects.');
      }
      setIsLoading(false);
    }
    loadProjects();
  }, []);

  return (
    <div className="container mx-auto max-w-6xl py-12 px-4">
      <div className="text-center mb-12">
        <h1 className="font-headline text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Our <GradientText>Projects</GradientText>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
          A showcase of our passion, expertise, and commitment to innovation.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="text-center text-destructive-foreground bg-destructive/80 p-4 rounded-md">
          <p className="font-bold">Failed to load projects.</p>
          <p className="text-sm">{error}</p>
        </div>
      ) : projects && projects.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) => (
            <Card key={project.id} className="flex flex-col overflow-hidden">
              <div className="aspect-video overflow-hidden">
                <Image
                  src={project.image_url || "https://placehold.co/600x400.png"}
                  alt={project.title}
                  width={600}
                  height={400}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  data-ai-hint="software project screenshot"
                />
              </div>
              <CardHeader>
                <CardTitle>{project.title}</CardTitle>
                <div className="flex flex-wrap gap-2 pt-2">
                  {project.technologies.split(',').map(tech => (
                    <Badge key={tech} variant="secondary">{tech.trim()}</Badge>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="flex-grow">
                <p className="text-muted-foreground">{project.description}</p>
              </CardContent>
              <CardFooter className="gap-2">
                {project.project_url && (
                  <Button asChild variant="outline" className="flex-1">
                    <Link href={project.project_url} target="_blank" rel="noopener noreferrer">
                      Live Demo
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                )}
                {project.source_code_url && (
                  <Button asChild className="flex-1">
                    <Link href={project.source_code_url} target="_blank" rel="noopener noreferrer">
                      Source Code
                    </Link>
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center text-muted-foreground py-16">
          <p className="text-lg">We are currently curating our public project showcase. Please check back soon!</p>
        </div>
      )}
    </div>
  );
}
